/**
 * PromoBanner — a centred CTA block on a brand-blue gradient with a faint grid,
 * inset as a rounded card on the page (DS core). Content-agnostic. White text;
 * pass a light DS Button (outline) as `action`. No raw [#hex]; rgba grid lines
 * are fine (channel colour). Named tokens for the gradient.
 */
import type { ReactNode } from "react";

export function PromoBanner({
  tag,
  eyebrow,
  title,
  body,
  action,
  tone = "blue",
}: {
  /** Pill above the eyebrow — e.g. which sectors the offer is aimed at. */
  tag?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  /** Gradient family — brand blue (default) or gold. White text either way. */
  tone?: "blue" | "gold";
}) {
  const gradient = tone === "gold"
    ? "linear-gradient(to bottom, var(--ds-warn), var(--ds-gold))"
    : "linear-gradient(to bottom, var(--ds-accent-deep), var(--ds-accent))";
  return (
    <section className="bg-bg font-ds">
      <div className="mx-auto max-w-content px-4 py-10 md:px-10 md:py-14">
        <div
          className="relative overflow-hidden rounded-3xl px-4 md:px-6 py-20 text-center lg:py-28"
          style={{ background: gradient }}
        >
          {/* faint grid overlay, fading toward the bottom */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              maskImage: "linear-gradient(to bottom, black, transparent)",
              WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
            }}
          />
          <div className="relative mx-auto flex max-w-2xl flex-col items-center gap-5">
            {/* Sector pill — translucent white so it reads as a label on the
                gradient without competing with the headline. Wraps on narrow
                screens rather than forcing the banner wider. */}
            {tag && (
              <span className="inline-flex max-w-full items-center rounded-full bg-white/15 px-4 py-1.5 text-center text-ds-14 font-semibold leading-snug text-white ring-1 ring-inset ring-white/25">
                {tag}
              </span>
            )}
            {eyebrow && <p className="text-ds-14 font-semibold uppercase tracking-[0.14em] text-white/70">{eyebrow}</p>}
            <h2 className="text-ds-38 font-semibold leading-[1.06] tracking-[-1px] text-white sm:text-ds-46 lg:text-ds-54">{title}</h2>
            {body && <p className="max-w-xl text-ds-18 leading-relaxed text-white/85">{body}</p>}
            {action && <div className="pt-3">{action}</div>}
          </div>
        </div>
      </div>
    </section>
  );
}
